import * as React from "react";
import {
  Card,
  CardContent,
  Grid as Grid2,
  Alert,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import dayjs, { Dayjs } from "dayjs";
import { useDataProvider, useNotify } from "react-admin";
import { formatEquipmentLabel } from "../utils/formatEquipment";

interface RemovalsStatsPanelProps {
  siteId: string;
}

type DailyStatPoint = {
  date: string;
  _0: number;
  _25: number;
  _50: number;
  _75: number;
  _100: number;
  _125: number;
  equivalentFullUnits: number;
};

export const RemovalsStatsPanel: React.FC<RemovalsStatsPanelProps> = ({
  siteId,
}) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [equipments, setEquipments] = React.useState<any[]>([]);
  const [loadingEquip, setLoadingEquip] = React.useState(true);
  const [errorEquip, setErrorEquip] = React.useState<string | null>(null);

  const [equipmentId, setEquipmentId] = React.useState<string>(""); // "" = tous
  const [from, setFrom] = React.useState<Dayjs>(
    dayjs().subtract(30, "day").startOf("day")
  );
  const [to, setTo] = React.useState<Dayjs>(dayjs().endOf("day"));

  const [loadingStats, setLoadingStats] = React.useState(false);
  const [errorStats, setErrorStats] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<DailyStatPoint[]>([]);

  const [visibleLines, setVisibleLines] = React.useState<
    Record<string, boolean>
  >({
    equivalentFullUnits: true,
    _0: true,
    _25: true,
    _50: true,
    _75: true,
    _100: true,
    _125: true,
  });

  const toggleLine = (dataKey: string) => {
    setVisibleLines((prev) => ({ ...prev, [dataKey]: !prev[dataKey] }));
  };

  React.useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingEquip(true);
      setErrorEquip(null);
      try {
        const { data } = await dataProvider.getList("equipments", {
          pagination: { page: 1, perPage: -1 },
          sort: { field: "_id", order: "DESC" },
          filter: {
            filterTree: {
              operator: "and",
              operands: [
                {
                  term: {
                    name: "site",
                    filterType: "eq",
                    value: siteId,
                  },
                },
              ],
            },
          },
        });
        if (!alive) return;
        setEquipments(data || []);
      } catch (e: any) {
        if (!alive) return;
        const msg = e?.message || "Erreur chargement équipements";
        setErrorEquip(msg);
        notify(msg, { type: "warning" });
      } finally {
        if (alive) setLoadingEquip(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [siteId, dataProvider, notify]);

  const fetchStats = React.useCallback(async () => {
    setLoadingStats(true);
    setErrorStats(null);
    try {
      let preFilterValue: any = [];
      if (siteId) {
        preFilterValue = JSON.stringify([{ name: "site", value: siteId }]);
      }

      const operands: any[] = [];
      if (from) {
        operands.push({
          term: {
            name: "createdAt",
            filterType: "gte",
            value: new Date(from.toString()).toISOString(),
          },
        });
      }
      if (to) {
        const end = new Date(to.toString());
        end.setHours(23, 59, 59, 999);
        operands.push({
          term: {
            name: "createdAt",
            filterType: "lte",
            value: end.toISOString(),
          },
        });
      }
      if (equipmentId) {
        operands.push({
          term: {
            name: "equipment",
            filterType: "eq",
            value: equipmentId,
          },
        });
      }

      const filter = {
        filterTree: {
          operator: "and",
          operands,
        },
      };
      const { data } = await dataProvider.getList("site-inventory", {
        pagination: { page: 1, perPage: -1 },
        sort: { field: "_id", order: "DESC" },
        meta: { preFilter: preFilterValue },
        filter,
      });

      const bucket: Record<
        string,
        {
          date: string;
          _0: number;
          _25: number;
          _50: number;
          _75: number;
          _100: number;
          _125: number;
          equivalentFullUnits: number;
          tonnage: number;
        }
      > = {};
      for (const rec of data) {
        const dIso = rec.scheduledAt || rec.createdAt;
        if (!dIso) continue;
        const dayKey = new Date(dIso).toISOString().slice(0, 10);

        if (!bucket[dayKey]) {
          bucket[dayKey] = {
            date: dayKey,
            _0: 0,
            _25: 0,
            _50: 0,
            _75: 0,
            _100: 0,
            _125: 0,
            equivalentFullUnits: 0,
            tonnage: 0,
          };
        }

        const c = rec.counts || {};
        bucket[dayKey]._0 += c._0 || 0;
        bucket[dayKey]._25 += c._25 || 0;
        bucket[dayKey]._50 += c._50 || 0;
        bucket[dayKey]._75 += c._75 || 0;
        bucket[dayKey]._100 += c._100 || 0;
        bucket[dayKey]._125 += c._125 || 0;

        bucket[dayKey].equivalentFullUnits += rec.equivalentFullUnits || 0;
        bucket[dayKey].tonnage += rec.tonnage || 0;
      }

      const aggregated = Object.values(bucket).sort((a, b) =>
        a.date < b.date ? -1 : a.date > b.date ? 1 : 0
      );

      console.log(aggregated)

      setRows(aggregated || []);
    } catch (e: any) {
      const msg = e?.message || "Erreur chargement stats";
      setErrorStats(msg);
    } finally {
      setLoadingStats(false);
    }
  }, [dataProvider, siteId, equipmentId, from, to]);

  React.useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <Card variant="outlined" sx={{ mt: 3, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Statistiques d'enlèvements
      </Typography>

      <CardContent sx={{ pb: 1 }}>
        <Grid2 container spacing={2} alignItems="center">
          <Grid2 size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth size="small" disabled={loadingEquip}>
              <InputLabel id="eq-select-lbl">Équipement</InputLabel>
              <Select
                labelId="eq-select-lbl"
                label="Équipement"
                value={equipmentId}
                onChange={(e) => setEquipmentId(String(e.target.value))}
              >
                <MenuItem value="">
                  <em>
                    {loadingEquip
                      ? "Chargement…"
                      : errorEquip
                      ? "Erreur"
                      : "Tous les équipements"}
                  </em>
                </MenuItem>

                {!loadingEquip &&
                  !errorEquip &&
                  equipments.map((eq: any) => (
                    <MenuItem key={eq.id || eq._id} value={eq.id || eq._id}>
                      {formatEquipmentLabel(eq)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <DatePicker
              label="Du"
              value={from}
              onChange={(v) => v && setFrom(v.startOf("day"))}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <DatePicker
              label="Au"
              value={to}
              onChange={(v) => v && setTo(v.endOf("day"))}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
              minDate={from}
            />
          </Grid2>
        </Grid2>
      </CardContent>

      <Box sx={{ width: "100%", height: 360, px: 1, pb: 2 }}>
        {loadingStats ? (
          <CircularProgress />
        ) : errorStats ? (
          <Alert severity="error">{errorStats}</Alert>
        ) : rows.length === 0 ? (
          <Alert severity="info">Aucune donnée sur la période.</Alert>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={rows}
              margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis allowDecimals />
              <Tooltip />
              <Legend
                onClick={(legendPayload: any) => {
                  if (!legendPayload || !legendPayload.dataKey) return;
                  toggleLine(legendPayload.dataKey);
                }}
                wrapperStyle={{ cursor: "pointer" }}
              />

              {visibleLines.equivalentFullUnits && (
                <Line
                  type="monotone"
                  dataKey="equivalentFullUnits"
                  name="Équiv. plein"
                  stroke="#000000"
                  strokeWidth={2}
                  dot={false}
                />
              )}

              {visibleLines._0 && (
                <Line
                  type="monotone"
                  dataKey="_0"
                  name="0%"
                  stroke="#9e9e9e"
                  strokeDasharray="4 4"
                  strokeWidth={2}
                  dot={false}
                />
              )}
              {visibleLines._25 && (
                <Line
                  type="monotone"
                  dataKey="_25"
                  name="25%"
                  stroke="#d32f2f"
                  strokeWidth={2}
                  dot={false}
                />
              )}
              {visibleLines._50 && (
                <Line
                  type="monotone"
                  dataKey="_50"
                  name="50%"
                  stroke="#ed6c02"
                  strokeWidth={2}
                  dot={false}
                />
              )}
              {visibleLines._75 && (
                <Line
                  type="monotone"
                  dataKey="_75"
                  name="75%"
                  stroke="#0288d1"
                  strokeWidth={2}
                  dot={false}
                />
              )}
              {visibleLines._100 && (
                <Line
                  type="monotone"
                  dataKey="_100"
                  name="100%"
                  stroke="#2e7d32"
                  strokeWidth={2}
                  dot={false}
                />
              )}
              {visibleLines._125 && (
                <Line
                  type="monotone"
                  dataKey="_125"
                  name="125% (débord.)"
                  stroke="#ad1457"
                  strokeDasharray="5 2"
                  strokeWidth={2}
                  dot={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>

      <Box sx={{ width: "100%", height: 360, px: 1, pb: 2 }}>
        {loadingStats ? (
          <CircularProgress />
        ) : errorStats ? (
          <Alert severity="error">{errorStats}</Alert>
        ) : rows.length === 0 ? (
          <Alert severity="info">Aucune donnée sur la période.</Alert>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={rows}
              margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis allowDecimals />
              <Tooltip />
              <Legend
                onClick={(legendPayload: any) => {
                  if (!legendPayload || !legendPayload.dataKey) return;
                  toggleLine(legendPayload.dataKey);
                }}
                wrapperStyle={{ cursor: "pointer" }}
              />

              <Line
                type="monotone"
                dataKey="tonnage"
                name="Tonnage"
                stroke="#2e7d32"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Card>
  );
};
